// MACHINERY V2 (Stroje) — the Servisné správy row kebab, as data (M2.5).
//
// Spec: data package §4 "Actions (+ gates)" + ticket §10. Reference impl:
// components/ServiceReportActionsMenu.tsx — this is that component's two status
// branches expressed as a list, so the v2 rows can feed the shared
// fleet/FleetCardMenu instead of growing a second menu component.
//
// THE GATES ARE COPIED, NOT REDESIGNED. Every one of them is the live rule:
//   Read  : Zobraziť detail · Zdieľať · Stiahnuť PDF   (no gate at all in v1)
//   Write : Vytvoriť žiadosť o servis · Označiť ako vyriešené
//   Edit  : canWrite OR isOwnReport — the Worker "own report" escape hatch
//           (ServiceReportActionsMenu.tsx:56-57: `isOwnReport =
//           createdByUserId === currentUserId`, `canEdit = canWrite ||
//           isOwnReport`). Applies to Upraviť (in progress) and Upraviť
//           riešenie (solved).
//   Admin : Vymazať (PermissionGuard(Machinery, Admin), and the server agrees —
//           DELETE /v1/customer/service-reports/{number} is Machinery/Admin per
//           data package §4 and §16).
// A report whose statusCode is neither 1 nor 2 gets NO menu — v1 returns null
// for it, and FleetCardMenu renders nothing for an empty list, so the behaviour
// survives the port for free.
//
// The artboard 1000 menu draws exactly these rows with their gate chips; the
// chips are a designer annotation of the gate and are not rendered in
// production (the gate hides the row instead of labelling it).
import { ServiceReport, ServiceReportStatusCode } from "../../../models/ServiceReportsModel";
import { FleetMenuAction } from "../../fleet/FleetCardMenu";

export interface SrActionGates {
  canWrite: boolean;
  canAdmin: boolean;
  /** `accountContext.user?.id` — undefined for a session with no user id, which
   *  makes `isOwnReport` false and leaves editing to canWrite alone. */
  currentUserId?: string;
}

export interface SrActionHandlers {
  onShowDetails: (r: ServiceReport) => void;
  onShare: (r: ServiceReport) => void;
  onDownloadPdf: (r: ServiceReport) => void;
  onCreateServiceRequest: (r: ServiceReport) => void;
  onEdit: (r: ServiceReport) => void;
  onMarkAsSolved: (r: ServiceReport) => void;
  onEditSolvedReport: (r: ServiceReport) => void;
  onDelete: (r: ServiceReport) => void;
}

export const buildSrActions = (
  report: ServiceReport,
  gates: SrActionGates,
  h: SrActionHandlers
): FleetMenuAction[] => {
  const status = report.statusCode;
  if (status !== ServiceReportStatusCode.inProgress && status !== ServiceReportStatusCode.solved) {
    return [];
  }

  const isOwnReport = !!gates.currentUserId && report.createdByUserId === gates.currentUserId;
  const canEdit = gates.canWrite || isOwnReport;
  const inProgress = status === ServiceReportStatusCode.inProgress;

  const actions: FleetMenuAction[] = [
    { id: "details", labelKey: "myMachinery:showDetails", onSelect: () => h.onShowDetails(report) },
    { id: "share", labelKey: "myMachinery:share", onSelect: () => h.onShare(report) },
    { id: "pdf", labelKey: "myMachinery:downloadPdf", onSelect: () => h.onDownloadPdf(report) },
  ];

  if (gates.canWrite) {
    actions.push({
      id: "rfs",
      labelKey: "myMachinery:createServiceRequest",
      onSelect: () => h.onCreateServiceRequest(report),
    });
  }

  if (inProgress) {
    if (canEdit) {
      actions.push({
        id: "edit",
        labelKey: "common:buttons.edit",
        onSelect: () => h.onEdit(report),
      });
    }
    if (gates.canWrite) {
      actions.push({
        id: "solve",
        labelKey: "myMachinery:markAsSolved",
        onSelect: () => h.onMarkAsSolved(report),
      });
    }
  } else if (canEdit) {
    actions.push({
      id: "editSolved",
      labelKey: "myMachinery:editSolvedReport",
      onSelect: () => h.onEditSolvedReport(report),
    });
  }

  if (gates.canAdmin) {
    actions.push({
      id: "delete",
      labelKey: "common:buttons.delete",
      danger: true,
      onSelect: () => h.onDelete(report),
    });
  }

  return actions;
};
